import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

const carouselItems = [
  {
    id: 1,
    bg: require("../../assets/images/querycards1.jpeg"),
    query: "Soft pastel color dresses for women for a day event.",
  },
  {
    id: 2,
    bg: require("../../assets/images/querycards2.jpeg"),
    query:
      "Vacation look for Women, Summer Dresses, Holiday Outfit matching accessories",
  },
  {
    id: 3,
    bg: require("../../assets/images/querycards3.jpeg"),
    query: " Professional and office-appropriate dresses and outfits for women",
  },
  {
    id: 4,
    bg: require("../../assets/images/querycards4.jpeg"),
    query: "Women 3 piece dress under Rs.15000",
  },
  {
    id: 5,
    bg: require("../../assets/images/querycards5.jpeg"),
    query: "Lightweight lawn fabric dresses for women for summer",
  },
  {
    id: 6,
    bg: require("../../assets/images/querycards6.jpeg"),
    query: " Formal and party shoes for men to match blue suit pants",
  },
  {
    id: 7,
    bg: require("../../assets/images/querycards7.jpeg"),
    query: " Matching printed dupatta for red plain dresses",
  },
  {
    id: 8,
    bg: require("../../assets/images/querycards8.jpeg"),
    query: "Men’s formal ties to match black suits and light blue shirt",
  },
  {
    id: 9,
    bg: require("../../assets/images/querycards9.jpeg"),
    query: "Kaftan dresses for her",
  },
  {
    id: 10,
    bg: require("../../assets/images/querycards10.jpeg"),
    query: "Mother’s Day gifts ideas",
  },
  {
    id: 11,
    bg: require("../../assets/images/querycards11.jpeg"),
    query: "Heavy Formal dresses for wedding season",
  },
  {
    id: 12,
    bg: require("../../assets/images/querycards12.jpeg"),
    query:
      "Women’s abaya collection including casual, embroidered, and luxury abayas",
  },
];

const FADE_DURATION = 800;
const SKELETON_HOLD = 600;
const AUTO_INTERVAL = 7000;

const QueryCards = ({ smallScreen = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const slidesToShow = isMobile ? 1 : 3;
  const rows = isMobile ? 3 : 1;
  const visibleCount = slidesToShow * rows;
  const total = carouselItems.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [contentOpacity, setContentOpacity] = useState(1);
  const [skeletonOpacity, setSkeletonOpacity] = useState(0);
  const isAnimating = useRef(false);
  const timerRef = useRef(null);

  const getVisibleItems = (startIndex) => {
    const items = [];
    for (let r = 0; r < rows; r++) {
      const rowItems = [];
      for (let c = 0; c < slidesToShow; c++) {
        const idx = (startIndex + r * slidesToShow + c) % total;
        rowItems.push(carouselItems[idx]);
      }
      items.push(rowItems);
    }
    return items;
  };

  const runTransition = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;

    setContentOpacity(0);
    setSkeletonOpacity(1);

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + visibleCount) % total);
    }, FADE_DURATION);

    setTimeout(() => {
      setSkeletonOpacity(0);
    }, FADE_DURATION + SKELETON_HOLD);

    setTimeout(
      () => {
        setContentOpacity(1);
        isAnimating.current = false;
      },
      FADE_DURATION + SKELETON_HOLD + FADE_DURATION,
    );
  };

  useEffect(() => {
    timerRef.current = setInterval(runTransition, AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [visibleCount, total]);

  const visibleRows = getVisibleItems(currentIndex);

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        px: isMobile ? 2 : 3,
        pt: isMobile ? 1.5 : 2,
        mb: isMobile ? 2 : 2.5,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Skeleton overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: skeletonOpacity,
            transition: `opacity ${FADE_DURATION}ms ease-in-out`,
            zIndex: skeletonOpacity > 0 ? 2 : 0,
            pointerEvents: "none",
          }}
        >
          {visibleRows.map((row, rIdx) => (
            <Box
              key={`skel-row-${rIdx}`}
              sx={{
                display: "flex",
                gap: isMobile ? 1.5 : 2.5,
                mb: rIdx < rows - 1 ? (isMobile ? 1.5 : 2.5) : 0,
              }}
            >
              {row.map((_, cIdx) => (
                <Skeleton
                  key={`skel-${rIdx}-${cIdx}`}
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    flex: 1,
                    height: isMobile ? 100 : isTablet ? 120 : 140,
                    borderRadius: "12px",
                    backgroundColor: "#f0f0f0",
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>

        {/* Content */}
        <Box
          sx={{
            opacity: contentOpacity,
            transition: `opacity ${FADE_DURATION}ms ease-in-out`,
          }}
        >
          {visibleRows.map((row, rIdx) => (
            <Box
              key={`row-${rIdx}`}
              sx={{
                display: "flex",
                gap: isMobile ? 1.5 : 2.5,
                mb: rIdx < rows - 1 ? (isMobile ? 1.5 : 2.5) : 0,
              }}
            >
              {row.map((item, cIdx) => (
                <Box
                  key={`${item.id}-${rIdx}-${cIdx}`}
                  onClick={() => {
                    navigate(`/search?q=${item.query}`);
                  }}
                  sx={{
                    flex: 1,
                    position: "relative",
                    backgroundImage: `url(${item.bg?.default || item.bg})`,
                    backgroundSize: "100% 100%",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    borderRadius: "12px",
                    height: isMobile ? 100 : isTablet ? 120 : 140,
                    cursor: "pointer",
                    overflow: "hidden",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default QueryCards;
